import {
  Alert,
  Avatar,
  Badge,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  IconButton,
  InputLabel,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  MenuItem,
  Paper,
  Select,
  Slider,
  Switch,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import React, { useState } from 'react';
import {
  HiPlus as Add,
  HiOfficeBuilding as Building,
  HiCalendar as Calendar,
  HiCheckCircle as CheckCircle,
  HiClock as Clock,
  HiTrash as Delete,
  HiPencil as Edit,
  HiUsers as Users,
} from 'react-icons/hi';
import { mockData } from './MockData';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`structure-tabpanel-${index}`}
      aria-labelledby={`structure-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const SchoolStructureSetup: React.FC = () => {
  const [tabValue, setTabValue] = useState(0);
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [dialogType, setDialogType] = useState('');
  const [_selectedItem, _setSelectedItem] = useState<any>(null);
  const [newItem, setNewItem] = useState<any>({});
  const [cycleConfig, setCycleConfig] = useState(mockData.cycleConfig);
  const [dailyStructure, setDailyStructure] = useState(mockData.dailyStructure);
  const [rooms, setRooms] = useState(mockData.rooms);
  const [yearGroups, setYearGroups] = useState(mockData.yearGroups);

  const handleTabChange = (_event: React.SyntheticEvent, newValue: number) => {
    setTabValue(newValue);
  };

  const openAddDialog = (type: string, item: any = {}) => {
    setDialogType(type);
    setNewItem(item);
    setShowAddDialog(true);
  };

  const handleSaveItem = () => {
    const itemWithId = { ...newItem, id: newItem.id || Date.now().toString() };

    switch (dialogType) {
      case 'period':
        setDailyStructure({
          ...dailyStructure,
          periods: dailyStructure.periods
            .map((p) => (p.id === itemWithId.id ? itemWithId : p))
            .concat(!newItem.id ? [itemWithId] : []),
        });
        break;
      case 'room':
        setRooms(newItem.id ? rooms.map((r) => (r.id === itemWithId.id ? itemWithId : r)) : [...rooms, itemWithId]);
        break;
      case 'yearGroup':
        setYearGroups(
          newItem.id
            ? yearGroups.map((yg) => (yg.id === itemWithId.id ? itemWithId : yg))
            : [...yearGroups, itemWithId],
        );
        break;
    }

    setShowAddDialog(false);
    setNewItem({});
  };

  const renderCycleConfiguration = () => (
    <Grid container spacing={3}>
      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Timetable Cycle Setup
            </Typography>
            <FormControlLabel
              control={
                <Switch
                  checked={cycleConfig.type === 'bi-weekly'}
                  onChange={(e) =>
                    setCycleConfig({
                      ...cycleConfig,
                      type: e.target.checked ? 'bi-weekly' : 'weekly',
                    })
                  }
                />
              }
              label="Bi-weekly cycle (Week A/B)"
            />
            <Box sx={{ mt: 2 }}>
              <Typography gutterBottom>Total periods per cycle</Typography>
              <Slider
                value={cycleConfig.totalPeriods}
                onChange={(_e, value) =>
                  setCycleConfig({
                    ...cycleConfig,
                    totalPeriods: value as number,
                  })
                }
                min={25}
                max={60}
                step={5}
                marks
                valueLabelDisplay="auto"
              />
            </Box>
            <Box sx={{ mt: 2 }}>
              {cycleConfig.weekLabels.map((label, index) => (
                <Chip key={index} label={label} color="primary" sx={{ mr: 1, mb: 1 }} />
              ))}
            </Box>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} md={6}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Cycle Variations
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Configure subjects with different patterns across cycle weeks
            </Typography>
            <List>
              {mockData.cycleVariations.map((variation) => (
                <ListItem key={variation.id}>
                  <ListItemText
                    primary={variation.subjectName}
                    secondary={`Week A: ${variation.weekA.periods}/week, Week B: ${variation.weekB.periods}/week`}
                  />
                  <ListItemSecondaryAction>
                    <IconButton size="small" color="primary">
                      <Edit />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
              ))}
            </List>
            <Button startIcon={<Add />} onClick={() => openAddDialog('variation')}>
              Add Cycle Variation
            </Button>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );

  const renderDailyStructure = () => (
    <Grid container spacing={3}>
      <Grid item xs={12} md={8}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Daily Timeline</Typography>
              <Button startIcon={<Add />} onClick={() => openAddDialog('period')}>
                Add Period
              </Button>
            </Box>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Period</TableCell>
                    <TableCell>Time</TableCell>
                    <TableCell>Duration</TableCell>
                    <TableCell>Type</TableCell>
                    <TableCell>Year Groups</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {dailyStructure.periods.map((period) => (
                    <TableRow key={period.id}>
                      <TableCell>{period.name}</TableCell>
                      <TableCell>
                        {period.startTime} - {period.endTime}
                      </TableCell>
                      <TableCell>{period.duration} mins</TableCell>
                      <TableCell>
                        <Chip
                          label={period.type}
                          color={period.type === 'lesson' ? 'primary' : 'secondary'}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>
                        {period.yearGroups.length > 3
                          ? `All (${period.yearGroups.length})`
                          : period.yearGroups.join(', ')}
                      </TableCell>
                      <TableCell>
                        <IconButton size="small" color="primary" onClick={() => openAddDialog('period', period)}>
                          <Edit />
                        </IconButton>
                        <IconButton size="small" color="error">
                          <Delete />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} md={4}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              School Day Summary
            </Typography>
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2" color="text.secondary">
                School hours: {dailyStructure.schoolStart} - {dailyStructure.schoolEnd}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Total periods: {dailyStructure.periods.filter((p) => p.type === 'lesson').length}
              </Typography>
              <Typography variant="body2" color="text.secondary">
                Break periods: {dailyStructure.periods.filter((p) => p.type === 'break').length}
              </Typography>
            </Box>
            <Divider sx={{ my: 2 }} />
            <Typography variant="subtitle2" gutterBottom>
              Period Types
            </Typography>
            {['lesson', 'break', 'lunch', 'form', 'assembly'].map((type) => (
              <Chip
                key={type}
                label={`${type} (${dailyStructure.periods.filter((p) => p.type === type).length})`}
                size="small"
                sx={{ mr: 1, mb: 1 }}
              />
            ))}
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );

  const renderRoomsAndFacilities = () => (
    <Grid container spacing={3}>
      <Grid item xs={12}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Classrooms & Facilities</Typography>
              <Button startIcon={<Add />} onClick={() => openAddDialog('room')}>
                Add Room
              </Button>
            </Box>
            <Grid container spacing={2}>
              {rooms.map((room) => (
                <Grid item xs={12} sm={6} md={4} key={room.id}>
                  <Card variant="outlined">
                    <CardContent>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 1 }}>
                        <Typography variant="subtitle1">{room.name}</Typography>
                        <Badge badgeContent={room.capacity} color="primary">
                          <Users />
                        </Badge>
                      </Box>
                      <Typography variant="body2" color="text.secondary" gutterBottom>
                        {room.type} • {room.location}
                      </Typography>
                      <Box sx={{ mb: 1 }}>
                        <Typography variant="caption" color="text.secondary">
                          Equipment:
                        </Typography>
                        {room.equipment.slice(0, 2).map((eq, idx) => (
                          <Chip key={idx} label={eq} size="small" sx={{ ml: 0.5, mb: 0.5 }} />
                        ))}
                        {room.equipment.length > 2 && (
                          <Chip label={`+${room.equipment.length - 2} more`} size="small" sx={{ ml: 0.5 }} />
                        )}
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 2 }}>
                        <Box>
                          {room.accessibility.length > 0 && <Chip label="Accessible" color="success" size="small" />}
                        </Box>
                        <Box>
                          <IconButton size="small" color="primary" onClick={() => openAddDialog('room', room)}>
                            <Edit />
                          </IconButton>
                          <IconButton size="small" color="error">
                            <Delete />
                          </IconButton>
                        </Box>
                      </Box>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
            </Grid>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );

  const renderYearGroupsAndClasses = () => (
    <Grid container spacing={3}>
      <Grid item xs={12} md={8}>
        <Card>
          <CardContent>
            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
              <Typography variant="h6">Year Groups & Class Structure</Typography>
              <Button startIcon={<Add />} onClick={() => openAddDialog('yearGroup')}>
                Add Year Group
              </Button>
            </Box>
            <TableContainer>
              <Table>
                <TableHead>
                  <TableRow>
                    <TableCell>Year Group</TableCell>
                    <TableCell>Students</TableCell>
                    <TableCell>Classes</TableCell>
                    <TableCell>Form Groups</TableCell>
                    <TableCell>Form Tutors</TableCell>
                    <TableCell>Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {yearGroups.map((yearGroup) => (
                    <TableRow key={yearGroup.id}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          <Avatar sx={{ bgcolor: 'primary.main', mr: 1, width: 32, height: 32 }}>
                            {yearGroup.yearNumber}
                          </Avatar>
                          {yearGroup.name}
                        </Box>
                      </TableCell>
                      <TableCell>{yearGroup.totalStudents}</TableCell>
                      <TableCell>{yearGroup.classes.length}</TableCell>
                      <TableCell>{yearGroup.formGroups.length}</TableCell>
                      <TableCell>
                        {yearGroup.formGroups.filter((fg) => fg.formTutor).length}/{yearGroup.formGroups.length}
                      </TableCell>
                      <TableCell>
                        <IconButton size="small" color="primary" onClick={() => openAddDialog('yearGroup', yearGroup)}>
                          <Edit />
                        </IconButton>
                        <IconButton size="small" color="error">
                          <Delete />
                        </IconButton>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} md={4}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Structure Summary
            </Typography>
            <Box sx={{ mb: 2 }}>
              <Paper sx={{ p: 2, textAlign: 'center', mb: 1 }}>
                <Typography variant="h4" color="primary">
                  {yearGroups.reduce((sum, yg) => sum + yg.totalStudents, 0)}
                </Typography>
                <Typography variant="body2">Total Students</Typography>
              </Paper>
              <Paper sx={{ p: 2, textAlign: 'center', mb: 1 }}>
                <Typography variant="h4" color="primary">
                  {yearGroups.reduce((sum, yg) => sum + yg.classes.length, 0)}
                </Typography>
                <Typography variant="body2">Total Classes</Typography>
              </Paper>
              <Paper sx={{ p: 2, textAlign: 'center' }}>
                <Typography variant="h4" color="primary">
                  {yearGroups.reduce((sum, yg) => sum + yg.formGroups.length, 0)}
                </Typography>
                <Typography variant="body2">Form Groups</Typography>
              </Paper>
            </Box>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );

  const renderValidation = () => (
    <Grid container spacing={3}>
      <Grid item xs={12} md={8}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Structure Validation
            </Typography>
            <Alert severity="success" sx={{ mb: 2 }}>
              <Typography variant="body2">✓ All year groups have valid class structures</Typography>
            </Alert>
            <Alert severity="success" sx={{ mb: 2 }}>
              <Typography variant="body2">✓ Room capacities match class sizes</Typography>
            </Alert>
            <Alert severity="warning" sx={{ mb: 2 }}>
              <Typography variant="body2">⚠ Year 8 missing form tutor for 8C</Typography>
            </Alert>
            <Alert severity="info" sx={{ mb: 2 }}>
              <Typography variant="body2">ℹ Daily structure allows for 6 teaching periods</Typography>
            </Alert>
          </CardContent>
        </Card>
      </Grid>
      <Grid item xs={12} md={4}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>
              Next Steps
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
              Your school structure is configured and ready for teacher assignments.
            </Typography>
            <Button variant="contained" fullWidth sx={{ mb: 1 }}>
              Save Structure
            </Button>
            <Button variant="outlined" fullWidth sx={{ mb: 1 }}>
              Export Configuration
            </Button>
            <Button variant="outlined" fullWidth>
              Continue to Teacher Management
            </Button>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );

  return (
    <Box sx={{ width: '100%', p: 3 }}>
      <Typography variant="h4" gutterBottom>
        School Structure Setup
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 3 }}>
        Configure your school's fundamental organizational framework including cycles, schedules, facilities, and
        student groupings
      </Typography>

      <Card>
        <CardContent>
          <Tabs value={tabValue} onChange={handleTabChange} aria-label="structure setup tabs">
            <Tab icon={<Calendar />} label="Timetable Cycles" />
            <Tab icon={<Clock />} label="Daily Structure" />
            <Tab icon={<Building />} label="Rooms & Facilities" />
            <Tab icon={<Users />} label="Year Groups & Classes" />
            <Tab icon={<CheckCircle />} label="Validation" />
          </Tabs>

          <TabPanel value={tabValue} index={0}>
            {renderCycleConfiguration()}
          </TabPanel>

          <TabPanel value={tabValue} index={1}>
            {renderDailyStructure()}
          </TabPanel>

          <TabPanel value={tabValue} index={2}>
            {renderRoomsAndFacilities()}
          </TabPanel>

          <TabPanel value={tabValue} index={3}>
            {renderYearGroupsAndClasses()}
          </TabPanel>

          <TabPanel value={tabValue} index={4}>
            {renderValidation()}
          </TabPanel>
        </CardContent>
      </Card>

      {/* Universal Add/Edit Dialog */}
      <Dialog open={showAddDialog} onClose={() => setShowAddDialog(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          {newItem.id ? 'Edit' : 'Add'}{' '}
          {dialogType === 'period'
            ? 'Period'
            : dialogType === 'room'
              ? 'Room'
              : dialogType === 'yearGroup'
                ? 'Year Group'
                : 'Item'}
        </DialogTitle>
        <DialogContent>
          {dialogType === 'period' && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Period Name"
                  value={newItem.name || ''}
                  onChange={(e) => setNewItem({ ...newItem, name: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Type</InputLabel>
                  <Select value={newItem.type || ''} onChange={(e) => setNewItem({ ...newItem, type: e.target.value })}>
                    <MenuItem value="lesson">Lesson</MenuItem>
                    <MenuItem value="break">Break</MenuItem>
                    <MenuItem value="lunch">Lunch</MenuItem>
                    <MenuItem value="form">Form Time</MenuItem>
                    <MenuItem value="assembly">Assembly</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField
                  fullWidth
                  label="Start Time"
                  type="time"
                  value={newItem.startTime || ''}
                  onChange={(e) => setNewItem({ ...newItem, startTime: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={6} sm={3}>
                <TextField
                  fullWidth
                  label="End Time"
                  type="time"
                  value={newItem.endTime || ''}
                  onChange={(e) => setNewItem({ ...newItem, endTime: e.target.value })}
                  InputLabelProps={{ shrink: true }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Duration (minutes)"
                  type="number"
                  value={newItem.duration || ''}
                  onChange={(e) => setNewItem({ ...newItem, duration: Number.parseInt(e.target.value) })}
                />
              </Grid>
            </Grid>
          )}

          {dialogType === 'room' && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Room Name"
                  value={newItem.name || ''}
                  onChange={(e) => setNewItem({ ...newItem, name: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <FormControl fullWidth>
                  <InputLabel>Room Type</InputLabel>
                  <Select value={newItem.type || ''} onChange={(e) => setNewItem({ ...newItem, type: e.target.value })}>
                    <MenuItem value="standard">Standard Classroom</MenuItem>
                    <MenuItem value="laboratory">Laboratory</MenuItem>
                    <MenuItem value="workshop">Workshop</MenuItem>
                    <MenuItem value="gymnasium">Gymnasium</MenuItem>
                    <MenuItem value="hall">Hall</MenuItem>
                    <MenuItem value="ict">ICT Suite</MenuItem>
                    <MenuItem value="art">Art Room</MenuItem>
                    <MenuItem value="music">Music Room</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Capacity"
                  type="number"
                  value={newItem.capacity || ''}
                  onChange={(e) => setNewItem({ ...newItem, capacity: Number.parseInt(e.target.value) })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Location"
                  value={newItem.location || ''}
                  onChange={(e) => setNewItem({ ...newItem, location: e.target.value })}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Equipment (comma separated)"
                  value={Array.isArray(newItem.equipment) ? newItem.equipment.join(', ') : ''}
                  onChange={(e) =>
                    setNewItem({
                      ...newItem,
                      equipment: e.target.value.split(',').map((item) => item.trim()),
                    })
                  }
                />
              </Grid>
            </Grid>
          )}

          {dialogType === 'yearGroup' && (
            <Grid container spacing={2} sx={{ mt: 1 }}>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Year Group Name"
                  value={newItem.name || ''}
                  onChange={(e) => setNewItem({ ...newItem, name: e.target.value })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Year Number"
                  type="number"
                  value={newItem.yearNumber || ''}
                  onChange={(e) => setNewItem({ ...newItem, yearNumber: Number.parseInt(e.target.value) })}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  fullWidth
                  label="Total Students"
                  type="number"
                  value={newItem.totalStudents || ''}
                  onChange={(e) => setNewItem({ ...newItem, totalStudents: Number.parseInt(e.target.value) })}
                />
              </Grid>
            </Grid>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowAddDialog(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleSaveItem}>
            {newItem.id ? 'Update' : 'Add'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default SchoolStructureSetup;
